import { useState, useEffect } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { Award, Plus, Edit, Trash2, Save, X, Building, DollarSign } from 'lucide-react';
import { fmtTRY } from '../utils/formatCurrency';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  PageHeader,
  ConfirmDialog,
  EmptyState,
  LoadingState,
} from '../components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCertificates() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', name_en: '', provider: '', exam_fee: 0 });

  // Confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/certificates/types`, { headers: authHeaders() });
      setCertificates(res.data.certificates || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCert = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/certificates/types/${editing.cert_id}`, form, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/certificates/types`, form, { headers: authHeaders() });
      }
      toast.success(editing ? t('admin.cert_updated') : t('admin.cert_added'));
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', name_en: '', provider: '', exam_fee: 0 });
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const requestDelete = (id) => {
    setConfirmId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmLoading(true);
    try {
      await axios.delete(`${API}/admin/certificates/types/${confirmId}`, { headers: authHeaders() });
      toast.success(t('admin.cert_deleted'));
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmId(null);
    }
  };

  const startEdit = (cert) => {
    setEditing(cert);
    setForm({ name: cert.name, name_en: cert.name_en || '', provider: cert.provider, exam_fee: cert.exam_fee });
    setShowForm(true);
  };

  return (
    <AdminLayout title="Certificate Manager" titleTr={t('admin.cert_page_title')}>
      <PageHeader
        title={t('admin.cert_page_title')}
        subtitle={t('admin.cert_page_subtitle')}
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('admin.cert_page_title') }]}
        actions={
          <button
            onClick={() => {
              setEditing(null);
              setForm({ name: '', name_en: '', provider: '', exam_fee: 0 });
              setShowForm(true);
            }}
            className="btn-primary px-4 py-2 text-sm flex items-center gap-1"
            data-testid="add-cert-btn"
          >
            <Plus size={16} /> {t('admin.cert_new')}
          </button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card-clay p-5 text-center">
          <Award size={24} className="mx-auto text-[var(--brand-secondary)] mb-2" />
          <p className="font-display text-2xl font-bold">{certificates.length}</p>
          <p className="text-xs font-bold text-[var(--text-muted)]">{t('admin.cert_stat_types')}</p>
        </div>
        <div className="card-clay p-5 text-center">
          <Building size={24} className="mx-auto text-sky-500 mb-2" />
          <p className="font-display text-2xl font-bold">
            {[...new Set(certificates.map(c => c.provider))].length}
          </p>
          <p className="text-xs font-bold text-[var(--text-muted)]">{t('admin.cert_stat_institutions')}</p>
        </div>
        <div className="card-clay p-5 text-center">
          <DollarSign size={24} className="mx-auto text-green-500 mb-2" />
          <p className="font-display text-2xl font-bold">
            {certificates.length > 0
              ? Math.round(certificates.reduce((s, c) => s + (c.exam_fee || 0), 0) / certificates.length)
              : 0}
          </p>
          <p className="text-xs font-bold text-[var(--text-muted)]">{t('admin.cert_stat_avg_fee')}</p>
        </div>
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-6"
          >
            <div className="card-clay p-6 space-y-4 border-2 border-[var(--brand-secondary)]">
              <div className="flex justify-between items-center">
                <h4 className="font-bold text-sm">
                  {editing ? t('admin.cert_edit') : t('admin.cert_new_form')}
                </h4>
                <button onClick={() => { setShowForm(false); setEditing(null); }}><X size={18} /></button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                    {t('admin.cert_name_tr')}
                  </label>
                  <input
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="MEB Onayli Sertifika"
                    className="w-full px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none"
                    data-testid="cert-name-tr"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                    {t('admin.cert_name_en')}
                  </label>
                  <input
                    value={form.name_en}
                    onChange={e => setForm({ ...form, name_en: e.target.value })}
                    placeholder="MEB Approved Certificate"
                    className="w-full px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                    {t('admin.cert_provider')}
                  </label>
                  <input
                    value={form.provider}
                    onChange={e => setForm({ ...form, provider: e.target.value })}
                    placeholder="Milli Eğitim Bakanlığı"
                    className="w-full px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none"
                    data-testid="cert-provider"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                    {t('admin.cert_exam_fee')}
                  </label>
                  <input
                    type="number"
                    value={form.exam_fee}
                    onChange={e => setForm({ ...form, exam_fee: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none"
                    data-testid="cert-exam-fee"
                  />
                </div>
              </div>
              <button
                onClick={saveCert}
                disabled={!form.name || !form.provider}
                className="btn-success px-6 py-2.5 w-full text-sm disabled:opacity-50"
                data-testid="save-cert-btn"
              >
                <Save size={14} className="inline mr-1" />
                {editing ? t('admin.cert_update') : t('admin.cert_save')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* List */}
      {loading ? (
        <LoadingState variant="table" rows={4} cols={4} />
      ) : certificates.length === 0 ? (
        <EmptyState
          icon={<Award size={40} strokeWidth={1.5} />}
          title={t('admin.cert_empty_title')}
          description={t('admin.cert_empty_desc')}
          action={{ label: t('admin.cert_new'), onClick: () => setShowForm(true) }}
        />
      ) : (
        <div className="grid gap-3">
          {certificates.map((cert, i) => (
            <motion.div
              key={cert.cert_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="card-clay p-5 flex items-center gap-4"
              data-testid={`cert-item-${i}`}
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[var(--brand-secondary)] to-yellow-300 flex items-center justify-center flex-shrink-0">
                <Award size={28} className="text-[var(--text-main)]" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm">{cert.name}</h4>
                {cert.name_en && <p className="text-[11px] text-[var(--text-muted)]">{cert.name_en}</p>}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] font-bold bg-sky-50 text-sky-700 px-2 py-0.5 rounded-lg flex items-center gap-1">
                    <Building size={10} /> {cert.provider}
                  </span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs font-bold text-[var(--text-muted)]">{t('admin.cert_exam_fee')}</p>
                <p className="font-display text-xl font-bold text-[var(--brand-primary)]">
                  {fmtTRY(cert.exam_fee)}
                </p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={() => startEdit(cert)}
                  className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                  data-testid={`edit-cert-${i}`}
                >
                  <Edit size={14} />
                </button>
                <button
                  onClick={() => requestDelete(cert.cert_id)}
                  className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                  data-testid={`delete-cert-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(o) => { if (!o) { setConfirmOpen(false); setConfirmId(null); } }}
        title={t('admin.cert_delete_title')}
        description={t('admin.cert_delete_desc')}
        variant="danger"
        confirmLabel={t('admin.btn_delete')}
        onConfirm={handleConfirmDelete}
        loading={confirmLoading}
      />
    </AdminLayout>
  );
}
